import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Send } from "lucide-react";
import { ModelloPicker } from "@/pages/abi/ModelloPicker";

const Opzione = ({ checked, onChange, label, hint, testid }) => (
  <label className="flex items-start gap-2.5 text-sm cursor-pointer py-1">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid={testid} className="mt-0.5" />
    <span><span className="text-slate-800">{label}</span>{hint && <span className="block text-xs text-slate-400">{hint}</span>}</span>
  </label>
);

export function AbiInvio({ id, open, onOpenChange, onSent }) {
  const [opt, setOpt] = useState(null);
  const [f, setF] = useState({ to: "", oggetto: "", messaggio: "", allega_dossier: true, includi_questionario: false, mup_pratica_id: "", documenti: [], firma_otp: false, cellulare: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get(`/abi/analyses/${id}/invio/opzioni`).then(({ data }) => {
      setOpt(data);
      setF((x) => ({ ...x, to: data.destinatari[0] || "", cellulare: data.cellulare || "", includi_questionario: data.ha_domande,
        oggetto: `Documentazione assicurativa — ${data.ragione_sociale}`,
        messaggio: "come concordato, le inviamo la documentazione relativa alla sua posizione assicurativa.\nRestiamo a disposizione per qualsiasi chiarimento.",
        mup_pratica_id: data.mup[0]?.pratica_id || "" }));
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [open, id]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const hasDoc = (code, tipo) => f.documenti.some((d) => d.prodotto_code === code && d.tipo === tipo);
  const toggleDoc = (code, tipo, on) => set("documenti", on ? [...f.documenti, { prodotto_code: code, tipo }] : f.documenti.filter((d) => !(d.prodotto_code === code && d.tipo === tipo)));

  const invia = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/abi/analyses/${id}/invio`, { ...f, origin: window.location.origin });
      toast.success(`Email inviata con ${data.n_allegati} allegati${f.firma_otp ? ": in attesa della conferma OTP del cliente" : ""}`);
      onOpenChange(false); onSent?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const prodotti = (opt?.prodotti || []).filter((p) => p.documenti.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="abi-invio-dialog">
        <DialogHeader>
          <DialogTitle>Invia al cliente</DialogTitle>
          <DialogDescription>Scegli i documenti da inviare via email. Nulla parte finché non premi "Invia".</DialogDescription>
        </DialogHeader>
        {!opt ? <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div> : (
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Destinatario (email o PEC, più indirizzi separati da virgola)</Label>
              <Input value={f.to} onChange={(e) => set("to", e.target.value)} list="abi-dest" data-testid="abi-invio-to" />
              <datalist id="abi-dest">{opt.destinatari.map((d) => <option key={d} value={d} />)}</datalist>
            </div>
            <ModelloPicker ragioneSociale={opt.ragione_sociale} onApply={({ oggetto, messaggio }) => setF((x) => ({ ...x, oggetto, messaggio }))} />
            <div className="space-y-1.5"><Label>Oggetto</Label><Input value={f.oggetto} onChange={(e) => set("oggetto", e.target.value)} data-testid="abi-invio-oggetto" /></div>
            <div className="space-y-1.5"><Label>Messaggio</Label><Textarea rows={f.messaggio.split("\n").length > 4 ? 10 : 3} value={f.messaggio} onChange={(e) => set("messaggio", e.target.value)} data-testid="abi-invio-messaggio" /></div>

            <div className="rounded-lg border border-slate-200 p-3">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Cosa inviare</p>
              <Opzione checked={f.allega_dossier} onChange={(v) => set("allega_dossier", v)} label="Dossier aziendale (PDF)" testid="abi-invio-dossier" />
              <Opzione checked={f.includi_questionario} onChange={(v) => set("includi_questionario", v)} label="Link al questionario"
                hint={opt.ha_domande ? "Il cliente risponde online alle domande del dossier" : "Non disponibile: nessuna domanda nel dossier (esegui l'analisi in modalità Assicurativa)"} testid="abi-invio-questionario" />
              <div className="flex items-center gap-2 py-1.5">
                <Checkbox checked={!!f.mup_pratica_id} disabled={!opt.mup.length} onCheckedChange={(v) => set("mup_pratica_id", v ? opt.mup[0]?.pratica_id : "")} data-testid="abi-invio-mup-check" />
                <span className="text-sm text-slate-800">MUP</span>
                {opt.mup.length ? (
                  <select value={f.mup_pratica_id} onChange={(e) => set("mup_pratica_id", e.target.value)} className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="abi-invio-mup">
                    <option value="">— nessuno —</option>
                    {opt.mup.map((m) => <option key={m.pratica_id} value={m.pratica_id}>{m.code} · {m.prodotto || "prodotto n/d"} · {m.state}</option>)}
                  </select>
                ) : <span className="text-xs text-slate-400">Nessun MUP per questo cliente</span>}
              </div>
              <p className="text-[11px] uppercase tracking-wide text-slate-400 mt-3 mb-1">Documenti di prodotto</p>
              {prodotti.length === 0 ? <p className="text-xs text-slate-400" data-testid="abi-invio-nodocs">Nessun CGA/DIP disponibile: caricali in Admin → Catalogo prodotti.</p> : prodotti.map((p) => (
                <div key={p.code} className="py-1" data-testid={`abi-invio-prod-${p.code}`}>
                  <p className="text-sm font-medium text-slate-700">{p.nome}</p>
                  <div className="flex flex-wrap gap-x-4">
                    {p.documenti.map((d) => <Opzione key={d.tipo} checked={hasDoc(p.code, d.tipo)} onChange={(v) => toggleDoc(p.code, d.tipo, v)} label={d.label} hint={d.fonte === "portale" ? "dal Portale" : null} testid={`abi-invio-doc-${p.code}-${d.tipo}`} />)}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-3 space-y-2">
              <div className="flex items-center gap-3"><Switch checked={f.firma_otp} onCheckedChange={(v) => set("firma_otp", v)} data-testid="abi-invio-otp" /><Label className="text-sm">Richiedi conferma di ricezione con OTP via SMS</Label></div>
              {f.firma_otp && <Input value={f.cellulare} onChange={(e) => set("cellulare", e.target.value)} placeholder="Cellulare del cliente (es. 333 1234567)" data-testid="abi-invio-cellulare" />}
              <p className="text-[11px] text-slate-500">Il cliente riceve un link, consulta i documenti e conferma con un codice SMS. Data, ora, IP e impronta dei documenti vengono registrati. È una conferma di ricezione, non una firma elettronica qualificata; la firma del MUP resta nel modulo MUP.</p>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button onClick={invia} disabled={sending || !opt} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="abi-invio-submit">
            {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Invia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
